import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';
import { sumKostnader } from '@shared/domain/calculations/budgetCalculations';
import type { SymfoniProofKey } from '@shared/data/proofCatalog';
import type { KostnadsPoster, WalletProvider } from '@shared/domain/types';

export type DemoRole = 'startup' | 'municipality';
export type AgreementType = 'cooperation' | 'loi';
export type PartnerSource = 'direct' | 'marketplace';
export type PartnerStatus = 'invited' | 'signed';
export type MarketplaceStatus = 'draft' | 'published' | 'closed';
export type DeMinimisStatus = 'idle' | 'checking' | 'verified' | 'unavailable';
export type ApplicationDecisionStatus = 'idle' | 'processing' | 'approved';

export interface DeMinimisCheckData {
  organizationNumber: string;
  status: DeMinimisStatus;
  previousSupport: number;
  requestedAmount: number;
  totalAmount: number;
  difference: number;
  isOverThreshold: boolean;
  checkedAt?: string;
  error?: string;
}

export interface Municipality {
  id: string;
  name: string;
  region: string;
}

export interface MunicipalityPartner {
  id: string;
  municipalityId: string;
  municipalityName: string;
  agreementType: AgreementType;
  source: PartnerSource;
  status: PartnerStatus;
  signedAt?: string;
}

export interface ProjectData {
  companyName: string;
  organizationNumber: string;
  title: string;
  shortDescription: string;
  problem: string;
  solution: string;
  desiredCollaboration: string;
}

export interface FundingPlanData {
  startupContribution: number;
  municipalityContribution: number;
  requestedGrant: number;
}

export interface FundingData {
  financing: FundingPlanData;
  startupCosts: KostnadsPoster;
  municipalityCosts: KostnadsPoster;
}

export interface FundingPatch {
  financing?: Partial<FundingPlanData>;
  startupCosts?: Partial<KostnadsPoster>;
  municipalityCosts?: Partial<KostnadsPoster>;
}

export interface DemoState {
  loggedIn: boolean;
  role: DemoRole;
  project: ProjectData;
  funding: FundingData;
  deMinimisCheck: DeMinimisCheckData;
  deMinimisRetryCount: number;
  walletProvider: WalletProvider;
  requestedProofKeys: SymfoniProofKey[];
  sentCredentials: string[];
  proofThreshold: number;
  partners: MunicipalityPartner[];
  marketplaceStatus: MarketplaceStatus;
  submitted: boolean;
  submittedAt?: string;
  decisionStatus: ApplicationDecisionStatus;
}

export function emptyDeMinimisCheck(requestedAmount: number): DeMinimisCheckData {
  return {
    organizationNumber: '',
    status: 'idle',
    previousSupport: 0,
    requestedAmount,
    totalAmount: requestedAmount,
    difference: 0,
    isOverThreshold: false,
  };
}

export type DemoAction =
  | { type: 'LOGIN' }
  | { type: 'RESET' }
  | { type: 'SET_ROLE'; role: DemoRole }
  | { type: 'UPDATE_PROJECT'; patch: Partial<ProjectData> }
  | { type: 'UPDATE_FUNDING'; patch: FundingPatch }
  | { type: 'RETRY_DE_MINIMIS_CHECK' }
  | { type: 'SET_DE_MINIMIS_CHECK'; check: DeMinimisCheckData }
  | { type: 'SET_WALLET_PROVIDER'; provider: WalletProvider }
  | { type: 'SET_REQUESTED_PROOF_KEYS'; proofKeys: SymfoniProofKey[] }
  | { type: 'SET_SENT_CREDENTIALS'; credentialIds: string[] }
  | { type: 'ADD_DIRECT_PARTNER'; municipality: Municipality; agreementType: AgreementType }
  | { type: 'REMOVE_PARTNER'; partnerId: string }
  | { type: 'SIGN_ALL_DIRECT_PARTNERS'; signedAt: string }
  | { type: 'PUBLISH_MARKETPLACE' }
  | { type: 'WITHDRAW_MARKETPLACE' }
  | { type: 'ADD_MARKETPLACE_LOI'; municipality: Municipality; signedAt: string }
  | { type: 'SUBMIT'; submittedAt: string }
  | { type: 'APPROVE_SUBMISSION' };

function containsMunicipality(state: DemoState, municipalityId: string): boolean {
  return state.partners.some((partner) => partner.municipalityId === municipalityId);
}

function nextMarketplaceStatus(
  currentStatus: MarketplaceStatus,
  nextSignedPartnerCount: number
): MarketplaceStatus {
  if (nextSignedPartnerCount >= 3 && currentStatus === 'published') {
    return 'closed';
  }
  return currentStatus;
}

function sanitizeAmount(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function sanitizeNumberPatch<T extends object>(patch: Partial<T>): Partial<T> {
  return Object.fromEntries(
    Object.entries(patch).map(([key, value]) => [key, sanitizeAmount(value as number)])
  ) as Partial<T>;
}

function reduceApplication(state: DemoState, action: DemoAction, createInitialState: () => DemoState): DemoState {
  if (
    state.submitted &&
    action.type !== 'RESET' &&
    action.type !== 'SET_ROLE' &&
    action.type !== 'APPROVE_SUBMISSION'
  ) {
    return state;
  }

  switch (action.type) {
    case 'LOGIN':
      return { ...state, loggedIn: true, role: 'startup' };
    case 'RESET':
      return createInitialState();
    case 'SET_ROLE':
      return { ...state, role: action.role };
    case 'UPDATE_PROJECT': {
      const project = { ...state.project, ...action.patch };
      const organizationChanged = project.organizationNumber.replace(/\D/g, '') !==
        state.project.organizationNumber.replace(/\D/g, '');
      return {
        ...state,
        project,
        deMinimisCheck: organizationChanged
          ? emptyDeMinimisCheck(state.funding.financing.requestedGrant)
          : state.deMinimisCheck,
      };
    }
    case 'UPDATE_FUNDING': {
      const funding = {
        financing: {
          ...state.funding.financing,
          ...(action.patch.financing
            ? sanitizeNumberPatch<FundingPlanData>(action.patch.financing)
            : {}),
        },
        startupCosts: {
          ...state.funding.startupCosts,
          ...(action.patch.startupCosts
            ? sanitizeNumberPatch<KostnadsPoster>(action.patch.startupCosts)
            : {}),
        },
        municipalityCosts: {
          ...state.funding.municipalityCosts,
          ...(action.patch.municipalityCosts
            ? sanitizeNumberPatch<KostnadsPoster>(action.patch.municipalityCosts)
            : {}),
        },
      };
      const requestedAmountChanged =
        funding.financing.requestedGrant !== state.funding.financing.requestedGrant;

      return {
        ...state,
        funding,
        deMinimisCheck: requestedAmountChanged
          ? emptyDeMinimisCheck(funding.financing.requestedGrant)
          : state.deMinimisCheck,
      };
    }
    case 'RETRY_DE_MINIMIS_CHECK':
      return {
        ...state,
        deMinimisRetryCount: state.deMinimisRetryCount + 1,
        deMinimisCheck: emptyDeMinimisCheck(state.funding.financing.requestedGrant),
      };
    case 'SET_DE_MINIMIS_CHECK':
      if (action.check.organizationNumber !== state.project.organizationNumber.replace(/\D/g, '') ||
          action.check.requestedAmount !== state.funding.financing.requestedGrant) return state;
      return { ...state, deMinimisCheck: action.check };
    case 'SET_WALLET_PROVIDER':
      if (state.walletProvider === action.provider) return state;
      return {
        ...state,
        walletProvider: action.provider,
        requestedProofKeys: [],
        sentCredentials: [],
      };
    case 'SET_REQUESTED_PROOF_KEYS':
      return { ...state, requestedProofKeys: [...action.proofKeys] };
    case 'SET_SENT_CREDENTIALS':
      return { ...state, sentCredentials: Array.from(new Set(action.credentialIds)) };
    case 'ADD_DIRECT_PARTNER': {
      const directPartnerCount = state.partners.filter((partner) => partner.source === 'direct').length;
      if (
        directPartnerCount >= 3 ||
        state.partners.length >= 3 ||
        containsMunicipality(state, action.municipality.id)
      ) {
        return state;
      }
      const partner: MunicipalityPartner = {
        id: `direct-${action.municipality.id}`,
        municipalityId: action.municipality.id,
        municipalityName: action.municipality.name,
        agreementType: action.agreementType,
        source: 'direct',
        status: 'invited',
      };
      const partners = [...state.partners, partner];
      return {
        ...state,
        partners,
      };
    }
    case 'REMOVE_PARTNER':
      return {
        ...state,
        partners: state.partners.filter((partner) => partner.id !== action.partnerId),
      };
    case 'SIGN_ALL_DIRECT_PARTNERS': {
      const partners: MunicipalityPartner[] = state.partners.map((partner) =>
          partner.source === 'direct' && partner.status === 'invited'
            ? { ...partner, status: 'signed', signedAt: action.signedAt }
            : partner
        );
      return {
        ...state,
        partners,
        marketplaceStatus: nextMarketplaceStatus(
          state.marketplaceStatus,
          signedPartnerCount(partners)
        ),
      };
    }
    case 'PUBLISH_MARKETPLACE':
      if (signedPartnerCount(state.partners) >= 3) return state;
      return { ...state, marketplaceStatus: 'published' };
    case 'WITHDRAW_MARKETPLACE':
      return { ...state, marketplaceStatus: 'draft' };
    case 'ADD_MARKETPLACE_LOI': {
      if (
        state.marketplaceStatus !== 'published' ||
        signedPartnerCount(state.partners) >= 3 ||
        containsMunicipality(state, action.municipality.id)
      ) {
        return state;
      }
      const partner: MunicipalityPartner = {
        id: `marketplace-${action.municipality.id}`,
        municipalityId: action.municipality.id,
        municipalityName: action.municipality.name,
        agreementType: 'loi',
        source: 'marketplace',
        status: 'signed',
        signedAt: action.signedAt,
      };
      const pendingDirectPartner = [...state.partners]
        .reverse()
        .find((candidate) => candidate.source === 'direct' && candidate.status === 'invited');
      const partners = [
        ...state.partners.filter((candidate) =>
          state.partners.length < 3 || candidate.id !== pendingDirectPartner?.id
        ),
        partner,
      ];
      return {
        ...state,
        partners,
        marketplaceStatus: nextMarketplaceStatus(
          state.marketplaceStatus,
          signedPartnerCount(partners)
        ),
      };
    }
    case 'SUBMIT': {
      const canSubmit =
        signedPartnerCount(state.partners) >= 3 &&
        state.deMinimisCheck.status === 'verified' &&
        state.deMinimisCheck.organizationNumber === state.project.organizationNumber.replace(/\D/g, '') &&
        state.deMinimisCheck.requestedAmount === state.funding.financing.requestedGrant &&
        !state.deMinimisCheck.isOverThreshold;
      if (!canSubmit) {
        return state;
      }
      return {
        ...state,
        submitted: true,
        submittedAt: action.submittedAt,
        decisionStatus: 'processing',
      };
    }
    case 'APPROVE_SUBMISSION':
      if (!state.submitted || state.decisionStatus !== 'processing') {
        return state;
      }
      return { ...state, decisionStatus: 'approved' };
  }
}

interface DemoContextValue {
  state: DemoState;
  dispatch: (action: DemoAction) => void;
  reset: () => void;
}

export function createApplicationStore(createInitialState: () => DemoState) {
  const demoReducer = (state: DemoState, action: DemoAction) => reduceApplication(state, action, createInitialState);
  const DemoContext = createContext<DemoContextValue | null>(null);

  function DemoProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(demoReducer, undefined, createInitialState);
    const reset = useCallback(() => dispatch({ type: 'RESET' }), []);
    const value = useMemo(() => ({ state, dispatch, reset }), [state, reset]);

    return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
  }

  function useDemo() {
    const context = useContext(DemoContext);
    if (!context) throw new Error('useDemo must be used within DemoProvider');
    return context;
  }

  return { DemoProvider, useDemo, demoReducer, createInitialState };
}

export function fundingTotal(funding: FundingData): number {
  return sumKostnader(funding.startupCosts) + sumKostnader(funding.municipalityCosts);
}

export function financingTotal(funding: FundingData): number {
  return Object.values(funding.financing).reduce(
    (total, value) => total + sanitizeAmount(value),
    0
  );
}

export function fundingDifference(funding: FundingData): number {
  return financingTotal(funding) - fundingTotal(funding);
}

export function signedPartnerCount(partners: MunicipalityPartner[]): number {
  return partners.filter((partner) => partner.status === 'signed').length;
}
